"use client";

import { useState } from "react";
import YouTube from "react-youtube";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Pencil } from "lucide-react";

import { useProfileDataStore } from "../../../store/useProfileDataStore";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile.ts";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { Form } from "@/components/ui/form.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { CoachMarker } from "@/components/CoachMarker.tsx";

interface YoutubePreviewProps {
  profileId: string;
  initialVideoUrl: string | undefined;
}

export default function YoutubePreview({ profileId, initialVideoUrl }: YoutubePreviewProps) {
  const [videoUrl, setVideoUrl] = useState<string | undefined>(initialVideoUrl);
  const [isError, setIsError] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(true);
  const { setToReload, profileData } = useProfileDataStore();
  const { t } = useTranslation(["profile", "common", "forms", "help"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const youtubeUrlSchema = z
    .string()
    .regex(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/,
      t("forms:errors.invalidYtUrl"),
    )
    .optional();
  const form = useForm({
    resolver: zodResolver(z.object({ youtube_url: youtubeUrlSchema })),
    defaultValues: { youtube_url: videoUrl || "" },
  });

  const { videoId, isShort } = getVideoInfo(videoUrl);

  const handleSubmit = async (values: { youtube_url: string }) => {
    try {
      await editTutorProfile(profileId, "about", { youtube_url: values.youtube_url });
      setVideoUrl(values.youtube_url);
      setIsEditDialogOpen(false);
      setToReload(true);
      toast.success(t("common:success"), {
        description: t("common:successMessage"),
      });
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    }
  };

  const renderVideoContent = () => (
    <div className={`relative w-full ${isShort ? "pb-[70%] xl:pb-[180.78%]" : "pb-[56.25%]"}`}>
      <YouTube
        className={`absolute top-0 left-0 ${isShort ? "h-full w-full" : "h-full w-full"}`}
        iframeClassName={`w-full h-full ${isShort ? "" : ""}`}
        videoId={videoId}
        onError={() => setIsError(true)}
      />
    </div>
  );

  const renderVideoOrSkeleton = () => {
    if (!videoUrl || !videoId || isError) {
      return (
        <CoachMarker
          description={t("help:coachMarkers.profileAddYoutube.description")}
          id="profile-add-youtube"
          placement="top"
          title={t("help:coachMarkers.profileAddYoutube.title")}
        >
          <div className={"rounded-md border border-dashed p-0.5"}>
            <div
              className="bg-foreground relative flex h-full w-full items-center justify-center rounded-md"
              style={{ minHeight: "200px" }}
            >
              <Button
                className={"bg-side-accent absolute top-0 left-0 h-full w-full"}
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
                {t("profile:aboutSectionEdit.editYt")}
              </Button>
            </div>
          </div>
        </CoachMarker>
      );
    }

    return (
      <div className="relative w-full">
        <div className="hidden xl:block">{renderVideoContent()}</div>
        <Collapsible
          className="w-full xl:hidden"
          open={isCollapsibleOpen}
          onOpenChange={setIsCollapsibleOpen}
        >
          <CollapsibleTrigger asChild>
            <Button className="flex w-full items-center justify-between pe-12" variant="outline">
              <span>{t("profile:aboutSection.youtube")}</span>
              {isCollapsibleOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>{renderVideoContent()}</CollapsibleContent>
        </Collapsible>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="absolute top-0 right-0 h-10 w-10"
                size="sm"
                variant="default"
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{t("common:edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  };

  return (
    <div className="bg-foreground mx-auto w-full rounded-md p-2">
      {renderVideoOrSkeleton()}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t("profile:aboutSectionEdit.youtubeUrl")}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
              <Input
                {...form.register("youtube_url")}
                placeholder={t("profile:basicInfo.youtubeUrlPlaceholder")}
              />
              {form.formState.errors.youtube_url && (
                <p className="text-destructive text-sm">
                  {form.formState.errors.youtube_url.message}
                </p>
              )}
              <Button type="submit">{t("common:save")}</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getVideoInfo(videoUrl: string | undefined) {
  if (!videoUrl) return { videoId: null, isShort: false };

  const videoIdMatch = videoUrl.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/,
  );
  const videoId = videoIdMatch ? videoIdMatch[1] : null;
  const isShort = videoUrl.includes("/shorts/");

  return { videoId, isShort };
}
